import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Plus, Trash2, X, Save } from "lucide-react";
import { toast } from "sonner";

const SHIFT_LABELS = { mattino: "Mattino", pomeriggio: "Pomeriggio", notte: "Notte" };

export const ChecklistTemplateEditor = ({ open, onClose, onSaved }) => {
  const [templates, setTemplates] = useState([]);
  const [activeShift, setActiveShift] = useState("mattino");
  const [sections, setSections] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!open) return;
    const load = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/checklists/templates`);
        setTemplates(res.data);
        const t = res.data.find((x) => x.shift === activeShift);
        setSections(t ? JSON.parse(JSON.stringify(t.sections)) : []);
      } catch {
        toast.error("Errore nel caricamento dei template");
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line
  }, [open]);

  const switchShift = (shift) => {
    setActiveShift(shift);
    const t = templates.find((x) => x.shift === shift);
    setSections(t ? JSON.parse(JSON.stringify(t.sections)) : []);
  };

  const updateSectionTitle = (si, value) => {
    const next = [...sections];
    next[si].title = value;
    setSections(next);
  };
  const addSection = () => setSections([...sections, { title: "", items: [] }]);
  const removeSection = (si) => setSections(sections.filter((_, i) => i !== si));

  const updateItem = (si, ii, field, value) => {
    const next = [...sections];
    next[si].items[ii][field] = value;
    setSections(next);
  };
  const addItem = (si) => {
    const next = [...sections];
    next[si].items.push({ text: "", time_hint: "", condition: "" });
    setSections(next);
  };
  const removeItem = (si, ii) => {
    const next = [...sections];
    next[si].items = next[si].items.filter((_, i) => i !== ii);
    setSections(next);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/checklists/templates/${activeShift}`, { sections });
      // refresh local templates cache
      const res = await axios.get(`${API}/checklists/templates`);
      setTemplates(res.data);
      toast.success(`Template ${SHIFT_LABELS[activeShift]} salvato`);
      onSaved && onSaved();
    } catch {
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="template-editor-dialog">
        <DialogHeader>
          <DialogTitle className="font-outfit">Modifica Check List</DialogTitle>
        </DialogHeader>

        {/* Shift switcher */}
        <div className="flex gap-2">
          {["mattino", "pomeriggio", "notte"].map((s) => (
            <button
              key={s}
              onClick={() => switchShift(s)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                activeShift === s ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-600 hover:bg-stone-200"
              }`}
              data-testid={`template-shift-${s}`}
            >
              {SHIFT_LABELS[s]}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto -mx-2 px-2 space-y-4 py-2">
          {loading ? (
            <p className="text-stone-400 text-sm py-8 text-center">Caricamento...</p>
          ) : (
            sections.map((section, si) => (
              <div key={si} className="border border-stone-200 rounded-xl p-3 bg-stone-50/50">
                <div className="flex items-center gap-2 mb-2">
                  <Input
                    value={section.title}
                    onChange={(e) => updateSectionTitle(si, e.target.value)}
                    placeholder="Titolo sezione"
                    className="flex-1 font-semibold"
                    data-testid={`section-title-${si}`}
                  />
                  <Button variant="ghost" size="icon" onClick={() => removeSection(si)} className="text-red-400 hover:text-red-600" data-testid={`remove-section-${si}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
                <div className="space-y-2">
                  {section.items.map((item, ii) => (
                    <div key={ii} className="bg-white rounded-lg border border-stone-200 p-2">
                      <div className="flex items-start gap-2">
                        <textarea
                          value={item.text}
                          onChange={(e) => updateItem(si, ii, "text", e.target.value)}
                          placeholder="Testo della voce..."
                          rows={2}
                          className="flex-1 text-sm resize-none border border-stone-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-amber-400"
                          data-testid={`item-text-${si}-${ii}`}
                        />
                        <Button variant="ghost" size="icon" onClick={() => removeItem(si, ii)} className="text-red-400 hover:text-red-600 flex-shrink-0" data-testid={`remove-item-${si}-${ii}`}>
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className="flex gap-2 mt-1.5">
                        <Input
                          value={item.time_hint || ""}
                          onChange={(e) => updateItem(si, ii, "time_hint", e.target.value)}
                          placeholder="Orario (es: 11:30)"
                          className="h-8 text-xs"
                          data-testid={`item-time-${si}-${ii}`}
                        />
                        <Input
                          value={item.condition || ""}
                          onChange={(e) => updateItem(si, ii, "condition", e.target.value)}
                          placeholder="Condizione (es: Solo Novotel)"
                          className="h-8 text-xs"
                          data-testid={`item-condition-${si}-${ii}`}
                        />
                      </div>
                    </div>
                  ))}
                  <Button variant="outline" size="sm" onClick={() => addItem(si)} className="w-full text-xs rounded-lg" data-testid={`add-item-${si}`}>
                    <Plus className="w-3.5 h-3.5 mr-1" /> Aggiungi voce
                  </Button>
                </div>
              </div>
            ))
          )}
          {!loading && (
            <Button variant="outline" onClick={addSection} className="w-full rounded-xl" data-testid="add-section-button">
              <Plus className="w-4 h-4 mr-1.5" /> Aggiungi sezione
            </Button>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Chiudi</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-stone-900 hover:bg-stone-800" data-testid="save-template-button">
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Salvataggio..." : "Salva"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ChecklistTemplateEditor;
